'use client'

import { type CookieAttributes, cookies } from '@repo/utils'
import { useCallback, useState } from 'react'

/**
 * Hook to read and write cookies.
 *
 * @param name The name of the cookie.
 * @param defaultValue The default value of the cookie.
 * @returns An object with the current value of the cookie, an `updateCookie` function to update its value and a `deleteCookie` function to delete the cookie.
 */
export function useCookie(
  name: string,
  defaultValue?: string
): {
  value: string | undefined
  updateCookie: (newValue: string, options?: CookieAttributes) => void
  deleteCookie: () => void
} {
  const [value, setValue] = useState<string | undefined>(() => {
    // If the cookie exists, return its value
    const cookie = cookies.get(name)
    if (cookie) return cookie

    // If the cookie does not exists and a default value is provided, set the cookie
    if (defaultValue) cookies.set(name, defaultValue)

    // Return the default value if it exists
    return defaultValue
  })

  /**
   * Update the value of the cookie.
   *
   * @param newValue The new value of the cookie.
   * @param options Additional options for the cookie.
   */
  const updateCookie = useCallback(
    (newValue: string, options?: CookieAttributes) => {
      cookies.set(name, newValue, options)

      setValue(newValue)
    },
    [name]
  )

  /**
   * Delete the cookie.
   */
  const deleteCookie = useCallback(() => {
    cookies.remove(name)

    setValue(undefined)
  }, [name])

  return {
    value,
    updateCookie,
    deleteCookie
  }
}
